"use client";
import useTranslate, { useTranslationMap } from "@app/_hooks/useTranslate";

import getStockStatusText from "@/utils/product/getStockStatusText";
import Link from "@components/Link";
import { Accordion } from "@components/ngg";
import Image from "next/image";
import { type PropsWithChildren } from "react";
import type { Variant } from "types/product";

import Icon from "./icon";

function LineItemLink({
  slug,
  locale,
  children,
  className,
}: PropsWithChildren<{ slug?: string; locale?: string; className?: string }>) {
  if (slug)
    return (
      <Link href={slug} locale={locale} className={className}>
        {children}
      </Link>
    );
  return <div className={className}>{children}</div>;
}

export default function Included({
  items,
  contentfulLocale,
  locale,
  className,
}: {
  items: Variant[];
  contentfulLocale?: string;
  locale?: string;
  className?: string;
}) {
  const t = useTranslate();
  const dictionary = useTranslationMap();
  const renderVariantStockStatus = (item: Variant) =>
    getStockStatusText({
      dictionary,
      locale: contentfulLocale,
      stockStatus: item.stockStatus,
      deliveryUnit: item.deliveryUnit,
      nextDeliveryDate: item.nextDeliveryDate,
      deliveryMin: item.deliveryMin?.toString(),
      deliveryMax: item.deliveryMax?.toString(),
    });

  return (
    <Accordion
      IconComponent={Icon}
      WrapperComponent="div"
      title={t("includedInThisPackage")}
      classNames={{
        wrapper: className,
        button: "text-left items-center leading-4 p-4 font-normal rounded-none",
        panel: "p-4",
      }}
      defaultOpen
      transitionProps={{ appear: false }}
    >
      <ul className="divide-y border">
        {items.map((item, index) => (
          <li key={item.id + item.title + item.ProductURL} className="">
            <LineItemLink
              slug={item.ProductURL}
              locale={locale}
              className="grid grid-cols-[auto_1fr]"
            >
              {item.image && (
                <div className="relative block min-h-24 w-24 shrink-0 bg-gray-100">
                  <Image
                    className="h-full w-full overflow-hidden object-contain p-1 mix-blend-multiply"
                    src={item.image.src}
                    alt={item.image.alt}
                    height={100}
                    width={100}
                  />
                </div>
              )}
              <div className="grid grid-flow-row gap-2 px-4 py-3">
                <div className="flex justify-between text-sm uppercase">
                  <span>{item.brand}</span>
                  <span className="underline">{t("details")}</span>
                </div>
                <div className="flex flex-1 flex-col overflow-hidden lg:flex-row lg:justify-between">
                  <span className="truncate">{item.title}</span>
                  {(items.at(index)?.quantity ?? 0) > 1 && (
                    <span>
                      {items.at(index)?.quantity}&nbsp;
                      {t("pieces")}
                    </span>
                  )}
                </div>
                <span className="block text-sm uppercase empty:hidden">
                  {renderVariantStockStatus(item)}
                </span>
              </div>
            </LineItemLink>
          </li>
        ))}
      </ul>
    </Accordion>
  );
}
